<template>
  <div>
    <b-card class="rounded-lg">
      <b-card-header>
        <h4>{{ $t('accounting_reports_title') }}</h4>
      </b-card-header>
      <b-card-body>
        <b-tabs content-class="mt-2">
          <b-tab :title="$t('accounting_reports_cash_flow')">
            <b-card class="p-0 filter-card rounded-lg mb-3">
              <b-row>
                <b-col
                  cols="12"
                  md="4"
                >
                  <b-form-group :label="$t('accounting_reports_from_date')">
                    <b-form-input
                      v-model="cashFlowFilters.date_from"
                      type="date"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  cols="12"
                  md="4"
                >
                  <b-form-group :label="$t('accounting_reports_to_date')">
                    <b-form-input
                      v-model="cashFlowFilters.date_to"
                      type="date"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  cols="12"
                  md="4"
                  class="d-flex align-items-end"
                >
                  <b-button
                    variant="primary"
                    @click="loadCashFlow"
                  >
                    {{ $t('accounting_reports_view') }}
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
            <div
              v-if="loadingCashFlow"
              class="text-center"
            >
              <b-spinner
                variant="primary"
                label="Spinning"
              />
            </div>
            <div v-else-if="cashFlowData">
              <b-card>
                <h5 class="mb-3">
                  {{ $t('accounting_reports_cash_flow_summary') }}
                </h5>
                <b-row>
                  <b-col
                    cols="12"
                    md="4"
                  >
                    <div class="text-center">
                      <p class="mb-0 text-muted">
                        {{ $t('accounting_reports_total_cash_in') }}
                      </p>
                      <h4 class="text-success">
                        {{ formatCurrency(cashFlowData.cash_in) }}
                      </h4>
                    </div>
                  </b-col>
                  <b-col
                    cols="12"
                    md="4"
                  >
                    <div class="text-center">
                      <p class="mb-0 text-muted">
                        {{ $t('accounting_reports_total_cash_out') }}
                      </p>
                      <h4 class="text-danger">
                        {{ formatCurrency(cashFlowData.cash_out) }}
                      </h4>
                    </div>
                  </b-col>
                  <b-col
                    cols="12"
                    md="4"
                  >
                    <div class="text-center">
                      <p class="mb-0 text-muted">
                        {{ $t('accounting_reports_net_cash_flow') }}
                      </p>
                      <h4>{{ formatCurrency(cashFlowData.net_cash_flow) }}</h4>
                    </div>
                  </b-col>
                </b-row>
              </b-card>
            </div>
          </b-tab>
          <b-tab :title="$t('accounting_reports_general_ledger')">
            <b-card class="p-0 filter-card rounded-lg mb-3">
              <b-row>
                <b-col
                  cols="12"
                  md="4"
                >
                  <b-form-group :label="$t('accounting_reports_from_date')">
                    <b-form-input
                      v-model="ledgerFilters.date_from"
                      type="date"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  cols="12"
                  md="4"
                >
                  <b-form-group :label="$t('accounting_reports_to_date')">
                    <b-form-input
                      v-model="ledgerFilters.date_to"
                      type="date"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  cols="12"
                  md="4"
                  class="d-flex align-items-end"
                >
                  <b-button
                    variant="primary"
                    @click="loadGeneralLedger"
                  >
                    {{ $t('accounting_reports_view') }}
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
            <div
              v-if="loadingLedger"
              class="text-center"
            >
              <b-spinner
                variant="primary"
                label="Spinning"
              />
            </div>
            <div v-else-if="ledgerData">
              <b-card>
                <h5 class="mb-3">
                  {{ $t('accounting_reports_entries') }}
                </h5>
                <b-table
                  v-if="ledgerData.entries && ledgerData.entries.length > 0"
                  striped
                  :items="ledgerData.entries"
                  :fields="ledgerFields"
                  responsive
                >
                  <template #cell(entry.entry_date)="data">
                    {{ formatDate(data.item.entry.entry_date) }}
                  </template>
                  <template #cell(entry.entry_type)="data">
                    <b-badge :variant="(data.item.entry.entry_type && data.item.entry.entry_type.value === 'debit') ? 'danger' : 'success'">
                      {{ $i18n.locale === 'ar' ? (data.item.entry.entry_type && data.item.entry.entry_type.label_ar) || '' : (data.item.entry.entry_type && data.item.entry.entry_type.label) || '' }}
                    </b-badge>
                  </template>
                  <template #cell(entry.amount)="data">
                    {{ formatCurrency(data.item.entry.amount) }}
                  </template>
                </b-table>
                <div
                  v-else
                  class="text-center text-muted py-4"
                >
                  {{ $t('accounting_reports_no_entries') }}
                </div>
              </b-card>
            </div>
          </b-tab>
        </b-tabs>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BRow,
  BCol,
  BSpinner,
  BTable,
  BFormGroup,
  BFormInput,
  BTabs,
  BTab,
} from 'bootstrap-vue'
import * as reportsAPI from '@/api/accounting/reports'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BRow,
    BCol,
    BSpinner,
    BTable,
    BFormGroup,
    BFormInput,
    BTabs,
    BTab,
  },
  data() {
    return {
      loadingCashFlow: false,
      loadingLedger: false,
      cashFlowData: null,
      ledgerData: null,
      cashFlowFilters: {
        date_from: null,
        date_to: null,
      },
      ledgerFilters: {
        date_from: null,
        date_to: null,
      },
      ledgerFields: [
        { key: 'account.code', label: this.$t('accounting_reports_account_code') },
        { key: 'account.name', label: this.$t('accounting_reports_account_name') },
        { key: 'entry.entry_date', label: this.$t('accounting_transactions_date') },
        { key: 'entry.entry_type', label: this.$t('accounting_transactions_type') },
        { key: 'entry.amount', label: this.$t('accounting_transactions_amount') },
      ],
    }
  },
  methods: {
    formatCurrency(amount, currency = 'EGP') {
      return new Intl.NumberFormat('ar-EG', {
        style: 'currency',
        currency,
      }).format(amount)
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString(this.$i18n.locale === 'ar' ? 'ar-EG' : 'en-US')
    },
    async loadCashFlow() {
      this.loadingCashFlow = true
      try {
        const params = {}
        if (this.cashFlowFilters.date_from) params.date_from = this.cashFlowFilters.date_from
        if (this.cashFlowFilters.date_to) params.date_to = this.cashFlowFilters.date_to
        const response = await reportsAPI.getCashFlowStatement(params)
        this.cashFlowData = response.data
      } catch (error) {
        console.error('Error loading cash flow:', error)
      } finally {
        this.loadingCashFlow = false
      }
    },
    async loadGeneralLedger() {
      this.loadingLedger = true
      try {
        const params = {}
        if (this.ledgerFilters.date_from) params.date_from = this.ledgerFilters.date_from
        if (this.ledgerFilters.date_to) params.date_to = this.ledgerFilters.date_to
        const response = await reportsAPI.getGeneralLedger(params)
        this.ledgerData = response.data
      } catch (error) {
        console.error('Error loading general ledger:', error)
      } finally {
        this.loadingLedger = false
      }
    },
  },
}
</script>

<style lang="scss">
</style>
